import capitalize from 'lodash/capitalize';
import { NextSeo } from 'next-seo';
import type { NextSeoProps } from 'next-seo/lib/types';
import { parse } from 'querystring';

import { addTrailingSlash, removeTrailingSlash } from '@/utils/urlUtils';

import type {
  ContentfulAsset,
  ContentfulSeoConfiguration,
} from '@/graphql/contentful/api';
import {
  getBrandConfig,
  LocaleConfiguration,
} from '@/lib/configuration/configuration';
import { BRAND } from '@/lib/const';
import { paths } from '@/lib/paths';
import { getRegionFromLocale } from '@/lib/regions';
import { SEO_HREFLANG_COUNTRIES } from '@/lib/seo/Config';
import { makeHrefLangs } from '@/lib/seo/Functions';
import { SEO_DEFAULTS } from '@/lib/seo/SeoVariables';

export interface SEOHeadProps {
  requestUrl: string | null;
  seoConfig?: Partial<ContentfulSeoConfiguration>;
}

export function cleanStoresPageParam(urlString: string): string {
  if (!urlString?.includes('/stores/breitling-boutiques/')) {
    return urlString;
  }

  try {
    const urlObj = new URL(urlString);

    const pageVal = urlObj.searchParams.get('page');
    if (pageVal) {
      const cleaned = pageVal.replace(/\/+/g, '');
      urlObj.searchParams.set('page', cleaned);
    }

    return urlObj.toString();
  } catch (err) {
    return urlString;
  }
}

function removeDuplicateLocales(urlPath: string, locale: string): string {
  const regex = new RegExp(`/${locale}/{1}`, 'g');
  const uniquePath = urlPath.replace(regex, `/${locale}/`); // Ensure a single occurrence of the locale
  return uniquePath.replace(new RegExp(`(/${locale})+`, 'g'), `/${locale}`);
}

const makeLanguageAlternates = (
  alternateLanguages: string[],
  locale: string,
  path: string,
  query: string,
) =>
  alternateLanguages.map((altLanguage) => {
    const pathWithoutLocale = path.replace(`/${locale}/`, '');
    const alternateLocale = altLanguage
      .toLowerCase()
      .split('-')
      .reverse()
      .join('-');
    let href = `${process.env.NEXT_PUBLIC_URL}${alternateLocale}/${pathWithoutLocale}${query}`;
    href = addTrailingSlash(href);

    return {
      href,
      hreflang: altLanguage,
    };
  });

const CANONICAL_QUERY_PARAMS = ['page'];

const filterQueryParameters = (
  queryString: string,
  allowedQueryParams: string[],
) => {
  if (!queryString) {
    return '';
  }

  const inputParams = new URLSearchParams(queryString);
  const outputParams = new URLSearchParams();

  // Copy only allowed params
  Array.from(inputParams.entries()).forEach(([key, value]) => {
    if (allowedQueryParams.includes(key)) {
      outputParams.append(key, value);
    }
  });

  // Sanitize the page parameter: keep leading digits, normalize to integer, drop if <= 1 or invalid
  if (outputParams.has('page')) {
    const rawPage = outputParams.get('page') ?? '';
    const match = String(rawPage).match(/^\d+/);
    const pageNumber = match ? parseInt(match[0], 10) : 0;
    if (!pageNumber || pageNumber <= 1) {
      outputParams.delete('page');
    } else {
      outputParams.set('page', String(pageNumber));
    }
  }

  const query = outputParams.toString();
  const finalQueryParams = query ? `?${query}` : '';

  return removeTrailingSlash(finalQueryParams);
};

// List of page paths that should exclude the title suffix
const EXCLUDED_PAGE_PATHS = [
  (countrySlug: string) =>
    paths.stores.breitlingBoutiques.asPath({ country: countrySlug }),
];

function isExcludedPage(pathname: string, countrySlug: string): boolean {
  return EXCLUDED_PAGE_PATHS.some((pathFn) =>
    pathname.endsWith(pathFn(countrySlug)),
  );
}

const TITLE_SUFFIX = `| ${capitalize(BRAND)}`;

// Function to append suffix to title if needed
export function appendTitleSuffix(
  title: string,
  pathname: string,
  countrySlug: string,
): string {
  if (title.trim() === '') return title;
  if (title?.includes(TITLE_SUFFIX)) return title;
  if (isExcludedPage(pathname, countrySlug)) return title;

  return `${title} ${TITLE_SUFFIX}`;
}

function makeNextSeoProps(
  seoTitle: string | null | undefined,
  seoDescription: string | null | undefined,
  seoIndex: boolean | null | undefined,
  seoCanonical: string | null | undefined,
  seoThumbnail: ContentfulAsset | null | undefined,
  requestUrl: string,
): NextSeoProps {
  const nextSeoProps: NextSeoProps = {};
  nextSeoProps.title = seoTitle || SEO_DEFAULTS.EN.title;
  nextSeoProps.description = seoDescription || SEO_DEFAULTS.EN.description;
  nextSeoProps.noindex = seoIndex === false;
  const url = new URL(requestUrl);

  const pathSegments = url.pathname.split('/').filter(Boolean);
  const locale = pathSegments[0];
  const cleanPath = removeDuplicateLocales(url.pathname, locale);
  const finalQueryParams = filterQueryParameters(
    url.search,
    CANONICAL_QUERY_PARAMS,
  );

  const currentUrl = `${addTrailingSlash(
    `${process.env.NEXT_PUBLIC_URL}${url.pathname.substring(1)}`,
  )}${finalQueryParams}`;

  switch (seoCanonical) {
    case 'watch':
    case 'strap':
      nextSeoProps.canonical = addTrailingSlash(
        `${process.env.NEXT_PUBLIC_URL}${cleanPath.substring(1)}`,
      );
      break;
    default:
      nextSeoProps.canonical = seoCanonical
        ? addTrailingSlash(
            `${process.env.NEXT_PUBLIC_URL}/${locale}/${seoCanonical}`.replace(
              /([^:]\/)\/+/g,
              '$1',
            ),
          )
        : currentUrl;
      break;
  }

  const metadataTags = [];
  metadataTags.push({
    name: 'viewport',
    content: 'width=device-width, initial-scale=1',
  });
  if (getBrandConfig().metaTags.length) {
    getBrandConfig().metaTags.forEach((tag) => {
      metadataTags.push({ name: tag.name, content: tag.content });
    });
  }

  nextSeoProps.openGraph = {
    title: nextSeoProps.title,
    description: nextSeoProps.description,
    url: currentUrl,
  };

  const thumbnailUrl = seoThumbnail?.url;
  const fallbackThumbnailUrl =
    process.env.NEXT_PUBLIC_URL + 'images/breitling.svg';

  metadataTags.push({
    name: 'thumbnail',
    content: thumbnailUrl ?? fallbackThumbnailUrl,
  });
  nextSeoProps.openGraph = {
    ...nextSeoProps.openGraph,
    images: [{ url: thumbnailUrl ?? fallbackThumbnailUrl }],
  };

  nextSeoProps.additionalMetaTags = metadataTags;
  const alternateLanguages = makeHrefLangs(
    LocaleConfiguration.getInstance().getSupportedCountries(),
    SEO_HREFLANG_COUNTRIES,
    locale?.split('-')[1],
  );

  nextSeoProps.additionalLinkTags = makeLanguageAlternates(
    alternateLanguages,
    locale,
    cleanPath,
    finalQueryParams,
  ).map(({ href, hreflang }) => ({
    rel: 'alternate',
    href,
    hreflang,
  }));

  const { country } = getRegionFromLocale(locale);

  const overwriteTitle = appendTitleSuffix(
    nextSeoProps.title ?? '',
    url.pathname,
    country.slug,
  );

  nextSeoProps.title = overwriteTitle;
  return nextSeoProps;
}

// index pagination and main page according to seoIndex value
export function getPageSeoIndexValue({ requestUrl, seoConfig }: SEOHeadProps) {
  if (seoConfig?.seoIndex === false) {
    return false;
  }

  const params = requestUrl?.split('?')[1];
  const queryParams = parse(params || '');

  const isMainPage = Object.keys(queryParams).length === 0;
  const isPaginatedPage =
    queryParams.page !== undefined && Object.keys(queryParams).length === 1;

  return isMainPage || isPaginatedPage;
}

const SEOHead: React.FC<SEOHeadProps> = ({ requestUrl, seoConfig }) => {
  if (!requestUrl) return null;

  const seoIndex = getPageSeoIndexValue({
    requestUrl,
    seoConfig,
  });

  if (!seoConfig) {
    const nextSeoProps = makeNextSeoProps(
      SEO_DEFAULTS.EN.title,
      SEO_DEFAULTS.EN.description,
      seoIndex,
      null,
      null,
      requestUrl,
    );

    return <NextSeo {...nextSeoProps} />;
  }

  const { seoCanonical, seoDescription, seoThumbnail, seoTitle } = seoConfig;

  const nextSeoProps = makeNextSeoProps(
    seoTitle,
    seoDescription,
    seoIndex,
    seoCanonical,
    seoThumbnail,
    requestUrl,
  );

  if (nextSeoProps.canonical) {
    nextSeoProps.canonical = cleanStoresPageParam(nextSeoProps.canonical);
  }

  return <NextSeo {...nextSeoProps} />;
};

export default SEOHead;
